import { Component, OnDestroy, inject } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';

import { AppFormFieldErrorDirective } from '@shared/directives/app-form-field-error.directive';
import { NotificationService } from '@shared/services/notifications/notification.service';
import { RecoverPasswordService } from './service/recover-password.service';
import { finalize } from 'rxjs';

@Component({
  selector: 'app-recover-password',
  standalone: true,
  imports: [ReactiveFormsModule, AppFormFieldErrorDirective],
  templateUrl: './recover-password.component.html',
  styleUrls: ['./recover-password.component.scss'],
})
export class RecoverPasswordComponent implements OnDestroy {
  private fb: FormBuilder = inject(FormBuilder);
  private recoverPasswordService: RecoverPasswordService = inject(RecoverPasswordService);
  private notificationService: NotificationService = inject(NotificationService);

  form: FormGroup = this.fb.group({
    email: ['', [Validators.required, Validators.email]],
  });
  submitting: boolean = false;
  emailSent: boolean = false;

  readonly RESEND_COOLDOWN_MS: number = 1 * 60 * 1000; // 1 minuto

  countdownRemainingMs: number = 0;
  private countdownInterval: ReturnType<typeof setInterval> | null = null;


  ngOnDestroy(): void {
    this.clearCountdown();
  }

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.submitting = true;

    this.recoverPasswordService
      .sendLinkToResetPassword(this.form.value.email)
      .pipe(finalize(() => (this.submitting = false)))
      .subscribe({
        next: () => {
          this.notificationService.show(
            'Success',
            'Enlace de recuperación enviado a tu correo',
            'success'
          );
          this.emailSent = true;
          this.startCountdown();
        },
        error: () => {
          this.notificationService.show(
            'Error',
            'Error al enviar el enlace de recuperación',
            'error'
          );
        },
      });
  }

  private startCountdown(): void {
    this.countdownRemainingMs = this.RESEND_COOLDOWN_MS;
    this.clearCountdown();
    this.countdownInterval = setInterval(() => {
      this.countdownRemainingMs = Math.max(0, this.countdownRemainingMs - 1000);
      if (this.countdownRemainingMs <= 0) this.clearCountdown();
    }, 1000);
  }

  private clearCountdown(): void {
    if (!this.countdownInterval) return;
    clearInterval(this.countdownInterval);
    this.countdownInterval = null;
  }

  formatTime(seconds: number): string {
    const minutes: number = Math.floor(seconds / 60);
    const secs: number = seconds % 60;
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  }

  get countdown(): number {
    return Math.ceil(this.countdownRemainingMs / 1000);
  }

  changeEmail(): void {
    this.emailSent = false;
    this.countdownRemainingMs = 0;
    this.clearCountdown();
  }
}
